import type { BillingInterval } from "./api.js";

const KEY = "nemo_billing_intent";

export interface BillingIntent {
  plan?: string;
  interval?: BillingInterval;
}

const planOk = (p: string | null): p is string => !!p && /^[a-z][a-z0-9_]{1,30}$/.test(p);

/** `plan` and `interval` from a marketing link (`#/signup?plan=explorer&interval=year`). Anything else is dropped. */
export function intentFromQuery(query: string): BillingIntent | null {
  const q = new URLSearchParams(query);
  const plan = q.get("plan");
  if (!planOk(plan)) return null;
  const interval = q.get("interval");
  return { plan, interval: interval === "month" || interval === "year" ? interval : undefined };
}

const KEEP_MS = 48 * 3_600_000;

/**
 * Remembered across sign-up and e-mail confirmation. The confirmation link usually opens in a new tab, which has its own session
 * storage, so this uses local storage with an expiry: a plan chosen on the website is not forgotten, and not kept for ever.
 */
export function rememberIntent(i: BillingIntent): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...i, at: Date.now() }));
  } catch {
    /* private window: the plan is simply not remembered */
  }
}

export function takeIntent(now = Date.now()): BillingIntent | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    localStorage.removeItem(KEY);
    const v = JSON.parse(raw) as BillingIntent & { at?: number };
    if (!planOk(v.plan ?? null) || typeof v.at !== "number" || now - v.at > KEEP_MS) return null;
    return { plan: v.plan, interval: v.interval === "month" || v.interval === "year" ? v.interval : undefined };
  } catch {
    return null;
  }
}
